import type { SQLiteDatabase } from 'expo-sqlite';

import { getSubtypeMeta, getSystemMeta } from '@/data/systems';

export type SearchKind =
  | 'system'
  | 'task'
  | 'inventory'
  | 'expense'
  | 'contact'
  | 'document'
  | 'quote'
  | 'goal'
  | 'note';

export interface SearchHit {
  kind: SearchKind;
  id: string;
  title: string;
  subtitle: string | null;
  meta: string | null;
  /** Higher = more relevant (we surface title-matches above body-matches). */
  rank: number;
}

interface Row extends SearchHit {}

/**
 * Cross-table fuzzy-ish search. Uses SQLite LIKE with `%term%` against the
 * fields that humans actually scan for. Title matches outrank body matches,
 * and each table contributes at most a few rows so a huge property doesn't
 * drown out the other categories.
 */
export async function searchEverything(
  db: SQLiteDatabase,
  propertyId: string,
  rawTerm: string
): Promise<SearchHit[]> {
  const term = rawTerm.trim();
  if (term.length < 2) return [];
  const like = `%${term.replace(/[%_]/g, (m) => `\\${m}`)}%`;

  const rows = await db.getAllAsync<Row>(
    `
    -- Systems: title rank 30 (nickname/kind/model), body rank 15 (notes)
    SELECT 'system' AS kind, s.id AS id,
           COALESCE(s.nickname, s.kind) AS title,
           s.kind || (CASE WHEN s.subtype IS NOT NULL THEN '|' || s.subtype ELSE '' END) AS subtitle,
           s.model AS meta,
           CASE WHEN s.nickname LIKE ? ESCAPE '\\' OR s.kind LIKE ? ESCAPE '\\' OR s.model LIKE ? ESCAPE '\\' THEN 30 ELSE 15 END AS rank
    FROM system s
    WHERE s.property_id = ? AND s.deleted_at IS NULL
      AND (s.nickname LIKE ? ESCAPE '\\' OR s.kind LIKE ? ESCAPE '\\'
           OR s.subtype LIKE ? ESCAPE '\\' OR s.model LIKE ? ESCAPE '\\'
           OR s.notes LIKE ? ESCAPE '\\')

    UNION ALL

    SELECT 'task' AS kind, t.id AS id,
           t.title AS title,
           t.rule_key AS subtitle,
           t.tier AS meta,
           CASE WHEN t.title LIKE ? ESCAPE '\\' THEN 30 ELSE 15 END AS rank
    FROM task t
    WHERE t.property_id = ? AND t.deleted_at IS NULL
      AND (t.title LIKE ? ESCAPE '\\' OR t.why LIKE ? ESCAPE '\\')

    UNION ALL

    SELECT 'inventory' AS kind, i.id AS id,
           i.name AS title,
           COALESCE(i.brand, i.room) AS subtitle,
           i.serial_number AS meta,
           CASE WHEN i.name LIKE ? ESCAPE '\\' OR i.brand LIKE ? ESCAPE '\\' THEN 30 ELSE 15 END AS rank
    FROM inventory_item i
    WHERE i.property_id = ? AND i.deleted_at IS NULL
      AND (i.name LIKE ? ESCAPE '\\' OR i.brand LIKE ? ESCAPE '\\'
           OR i.model LIKE ? ESCAPE '\\' OR i.serial_number LIKE ? ESCAPE '\\'
           OR i.room LIKE ? ESCAPE '\\' OR i.notes LIKE ? ESCAPE '\\')

    UNION ALL

    SELECT 'expense' AS kind, e.id AS id,
           COALESCE(e.description, e.category) AS title,
           e.vendor AS subtitle,
           e.category AS meta,
           CASE WHEN e.description LIKE ? ESCAPE '\\' OR e.vendor LIKE ? ESCAPE '\\' THEN 30 ELSE 15 END AS rank
    FROM expense e
    WHERE e.property_id = ? AND e.deleted_at IS NULL
      AND (e.description LIKE ? ESCAPE '\\' OR e.vendor LIKE ? ESCAPE '\\'
           OR e.category LIKE ? ESCAPE '\\' OR e.notes LIKE ? ESCAPE '\\')

    UNION ALL

    SELECT 'contact' AS kind, c.id AS id,
           c.name AS title,
           COALESCE(c.role, c.company) AS subtitle,
           c.phone AS meta,
           CASE WHEN c.name LIKE ? ESCAPE '\\' THEN 30 ELSE 15 END AS rank
    FROM contact c
    WHERE c.property_id = ? AND c.deleted_at IS NULL
      AND (c.name LIKE ? ESCAPE '\\' OR c.role LIKE ? ESCAPE '\\'
           OR c.company LIKE ? ESCAPE '\\' OR c.phone LIKE ? ESCAPE '\\'
           OR c.email LIKE ? ESCAPE '\\' OR c.notes LIKE ? ESCAPE '\\')

    UNION ALL

    SELECT 'document' AS kind, d.id AS id,
           d.title AS title,
           d.vendor AS subtitle,
           d.kind AS meta,
           CASE WHEN d.title LIKE ? ESCAPE '\\' THEN 30 ELSE 15 END AS rank
    FROM document d
    WHERE d.property_id = ? AND d.deleted_at IS NULL
      AND (d.title LIKE ? ESCAPE '\\' OR d.vendor LIKE ? ESCAPE '\\'
           OR d.notes LIKE ? ESCAPE '\\')

    UNION ALL

    SELECT 'quote' AS kind, q.id AS id,
           COALESCE(q.project, q.vendor, q.category) AS title,
           q.vendor AS subtitle,
           q.status AS meta,
           CASE WHEN q.project LIKE ? ESCAPE '\\' OR q.vendor LIKE ? ESCAPE '\\' THEN 30 ELSE 15 END AS rank
    FROM quote q
    WHERE q.property_id = ? AND q.deleted_at IS NULL
      AND (q.project LIKE ? ESCAPE '\\' OR q.vendor LIKE ? ESCAPE '\\'
           OR q.description LIKE ? ESCAPE '\\' OR q.notes LIKE ? ESCAPE '\\')

    UNION ALL

    SELECT 'goal' AS kind, g.id AS id,
           g.title AS title,
           g.category AS subtitle,
           g.status AS meta,
           CASE WHEN g.title LIKE ? ESCAPE '\\' THEN 30 ELSE 15 END AS rank
    FROM goal g
    WHERE g.property_id = ? AND g.deleted_at IS NULL
      AND (g.title LIKE ? ESCAPE '\\' OR g.why LIKE ? ESCAPE '\\'
           OR g.notes LIKE ? ESCAPE '\\')

    UNION ALL

    SELECT 'note' AS kind, n.id AS id,
           COALESCE(n.title, SUBSTR(n.body, 1, 60)) AS title,
           NULL AS subtitle,
           NULL AS meta,
           CASE WHEN n.title LIKE ? ESCAPE '\\' THEN 30 ELSE 15 END AS rank
    FROM note n
    WHERE n.property_id = ? AND n.deleted_at IS NULL
      AND (n.title LIKE ? ESCAPE '\\' OR n.body LIKE ? ESCAPE '\\')

    ORDER BY rank DESC, title COLLATE NOCASE ASC
    LIMIT 80
    `,
    // system: title-rank (3) + property + body (5)
    like, like, like, propertyId, like, like, like, like, like,
    // task: title-rank (1) + property + body (2)
    like, propertyId, like, like,
    // inventory: title-rank (2) + property + body (6)
    like, like, propertyId, like, like, like, like, like, like,
    // expense: title-rank (2) + property + body (4)
    like, like, propertyId, like, like, like, like,
    // contact: title-rank (1) + property + body (6)
    like, propertyId, like, like, like, like, like, like,
    // document: title-rank (1) + property + body (3)
    like, propertyId, like, like, like,
    // quote: title-rank (2) + property + body (4)
    like, like, propertyId, like, like, like, like,
    // goal: title-rank (1) + property + body (3)
    like, propertyId, like, like, like,
    // note: title-rank (1) + property + body (2)
    like, propertyId, like, like
  );

  return rows.map((r) => {
    if (r.kind !== 'system') return r;
    const [rawKind, rawSub] = (r.subtitle ?? '').split('|');
    const meta = getSystemMeta(rawKind ?? '');
    const subtype = rawSub ? getSubtypeMeta(rawKind, rawSub) : null;
    return {
      ...r,
      title: r.title?.trim() || meta?.label || rawKind || 'System',
      subtitle: subtype?.label ?? meta?.label ?? null,
    };
  });
}
